import React, {
    useEffect,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import parse from 'html-react-parser';

import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';

import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';

import {StoreState} from '~/frontend/src/store';
import {XhrState} from '~/frontend/src/reducers/xhr';
import clearAjaxError from '~/frontend/src/actions/ajax/clearAjaxError';

type ErrorType<A> = A extends { [ key : string ] : infer V } ? V : never;

type DefaultProps = {
    dataContext? : string;
}
const defaultProps : Required<DefaultProps> = {
    dataContext: 'global'
};

type Props = {

    maskParentOnly? : boolean;

    style? : React.CSSProperties;
};

//type ComponentProps = DefaultProps & Props;
type AllProps = Required<DefaultProps> & Props;


/**
 * Error dialog
 * @return {JSX.Element}
 * @constructor
 */
const XhrErrorDialog = ( props : AllProps ) => {
    const dispatch          = useDispatch(),
          {
              dataContext,
              style,
              maskParentOnly
          }                 = props as AllProps,
          errors            = useSelector<StoreState, XhrState[ 'errors' ]>( ( state : StoreState ) => state.xhr?.errors ),
          errorComponentUid = useMemo(
              () => {
                  let retVal : string = '';

                  if ( errors ) {
                      const errorComponentUids = Object.keys( errors ) as string[];

                      if ( errorComponentUids.includes( dataContext ) ) {
                          retVal = dataContext;
                      }
                      else {
                          for ( const componentUid of errorComponentUids ) {
                              if ( componentUid.match( `^${dataContext}_(.*)$` ) ) {
                                  retVal = componentUid;
                                  break;
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  errors
              ]
          ),
          error             = useMemo(
              () => errors?.[ errorComponentUid ],
              [
                  errors
              ]
          ),
          maskClassName     = useMemo(
              () => maskParentOnly ? 'yn-dialog-panel-modal' : undefined,
              [
                  maskParentOnly
              ]
          ),
          footer            = useMemo(
              () => <div>
                  <Button label="OK" className="p-button-outlined" onClick={() => dispatch( clearAjaxError( errorComponentUid ) )}/>
              </div>,
              [
                  errorComponentUid
              ]
          ),
          onDocumentKeyDown = ( event : KeyboardEvent ) => {
              if ( error && event.key === 'Enter' ) {
                  dispatch( clearAjaxError( errorComponentUid ) );
                  event.preventDefault();
                  event.stopPropagation();
                  event.stopImmediatePropagation();
              }
          };

    // on mount / unmount
    useEffect(
        () => {
            document.addEventListener( 'keydown', onDocumentKeyDown, true );

            return () => {
                document.removeEventListener( 'keydown', onDocumentKeyDown, true );
            };
        },
        []
    );

    return useMemo(
        () => {
            let retVal : React.ReactNode | null = null;

            if ( error ) {
                const {
                          title,
                          message,
                          code,
                          errors
                      } = error;

                let transformedMessage = '';

                if ( message ) {
                    transformedMessage += message.replace( /\r?\n/g, '<br/>' );
                }

                if ( code ) {
                    if ( transformedMessage !== '' ) {
                        transformedMessage += '<br/>';
                    }
                    transformedMessage += `error number: ${code}`;
                }

                if ( message || code ) {
                    transformedMessage += '<br/><hr className="yn-error-ruler" />';
                }

                if ( Array.isArray( errors ) && errors?.length ) {
                    for ( const subError of errors ) {
                        if ( typeof subError === 'string' ) {
                            transformedMessage += '<br/>' + String( subError );
                        }
                        else if ( subError.type === 'stack' ) {
                            transformedMessage += StackError.fromJSON( subError ).toHTML();
                        }
                        else if ( subError.type === 'localizable' ) {
                            transformedMessage += LocalizableError.fromJSON( subError ).toHTML();
                        }
                        else {
                            transformedMessage += Error.fromJSON( subError ).toHTML();
                        }
                    }
                }

                retVal = <Dialog
                    header={title}
                    footer={footer}
                    maskClassName={maskClassName}
                    appendTo={maskParentOnly ? 'self' : null}
                    baseZIndex={99999}
                    visible={true}
                    style={style}
                    closable={false}
                    focusOnShow={true}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}>
                    {parse( transformedMessage )}
                </Dialog>;
            }

            return retVal;
        },
        [
            error
        ]
    );
}

XhrErrorDialog.defaultProps = defaultProps;

export default XhrErrorDialog;
